import React, { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, doc, FirestoreError, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { ArrowLeft, Download, Edit } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import ModulesTab from '../components/course/ModulesTab';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import { query, where } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { app } from '../firebase';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useLoading } from '../hooks/useLoading';


const CourseDetails = () => {
  const { toast } = useToast();
  const { courseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState<any>(null);
  const db = getFirestore(app);
  const auth = getAuth(app);
  const { setIsLoading } = useLoading();
  const [isLoading, setISLoading] = useState(false);

  const [batches, setBatches] = useState([]);

  // Fetch course data on component mount  
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const courseDoc = await getDoc(doc(db, "courses", courseId));
        if (courseDoc.exists()) {
          const courseData = { id: courseDoc.id, ...courseDoc.data() }; // Include ID with course data    
          setCourse(eliminateDuplicateModuleIds(courseData)); // Set the course state to the fetched data with ID  
        } else {
          toast({
            title: "Course not found",
            description: "The requested course does not exist.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
        toast({
          title: "Error fetching course",
          description: "There was an error loading the course details.",
          variant: "destructive",
        });
      } finally {
        setLoading(false); // Stop loading  
      }
    };

    const fetchBatchesData = async () => {
      setLoading(true); // Start loading before fetching data  

      try {
        const batchesCollection = collection(db, 'courseBatches'); // Reference the collection  
        const querySnapshot = await getDocs(batchesCollection); // Get all documents in the collection  

        // Map the documents to an array  
        const batchesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),

        }));

        setBatches(batchesData.sort(sortByBatchName)); // Update state with batches  
      } catch (error: any) {
        if (error instanceof FirestoreError) {
          toast({
            title: "Error",
            description: "Failed to fetch batch data.",
            variant: "destructive",
          });
        }
      } finally {
        setLoading(false); // Stop loading after the attempt  
      }
    };




    fetchCourseData();
    fetchBatchesData();
  }, [courseId, db, toast]);


  const eliminateDuplicateModuleIds = useCallback((course) => {
    course?.subjects?.forEach(subject => {
      const uniqueModuleIds = Array.from(new Set(subject?.moduleIds));
      subject.moduleIds = uniqueModuleIds; // Update the subject's moduleIds to unique values
    });
    setCourse(course);
    return course;
  }, [course]); // Add course as a dependency


  const sortByBatchName = (a: any, b: any) => {
    if (a.batchName < b.batchName) {
      return -1; // a should come before b  
    }
    if (a.batchName > b.batchName) {
      return 1; // a should come after b  
    }
    return 0; // a and b are equal  
  };


  // Handle course syllabus download
  const handleSyllabusDownload1 = async () => {
    setLoading(true);
    setIsLoading(true);
    if (!course?.syllabusMaterial?.url || course?.syllabusMaterial?.url == "") {
      toast({
        title: "Error fetching course data",
        description: "File Not Exists",
        variant: "destructive",
      });
      return;
    }
    const fileUrl = course?.syllabusMaterial?.url;
    //setIsLoading(true);
    // setProgress(0);

    try {
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            //setProgress(percentDone);
          }
        }
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, fileUrl.includes('assets') ? fileUrl.split('/').pop() : course?.syllabusMaterial?.fileName); // Save the file with the last part of the URL  
      //  setProgress(100); // Set progress to 100% on success  
      // setIsLoading(false);
      setLoading(false);
      setIsLoading(false);
    } catch (error) {
      setLoading(false);
      setIsLoading(false);
      console.error('Download error:', error);
      // setProgress(0); // Reset progress on error  
    } finally {
      // setIsLoading(false);
    }
  };


  // Sample data structure for now if the fetching fails  
  const defaultCourse = {
    id: courseId || '1',
    title: 'Loading...',
    description: 'Fetching course information...',
    category: '',
    duration: 'N/A',
    level: 'N/A',
    price: 0,
    instructor: 'N/A',
    subjects: [], // Changed to 'subjects' for consistency with the data structure  
  };

  // If loading or course is not available, show default  
  if (loading) return <div className="flex justify-center items-center h-full">Loading...</div>;
  if (!course) setCourse(defaultCourse); // Fallback to default course  

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Course Details" subtitle="Manage course modules and lessons" />
      <div className="mb-0 mt-6 ml-6">
        <Link to="/courses" className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Courses
        </Link>
      </div>
      <div className="flex-1 p-4 md:p-8 animate-fade-in overflow-auto">
        <Card className="mb-6">
          <CardHeader className="bg-bytesplash-blue text-white py-6">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <CardTitle className="text-2xl font-bold">{course?.title ?? course?.courseName}
              </CardTitle>

              <Button variant="secondary" size="sm" asChild>
                <Link to={`/courses/edit/${course?.id}`}>
                  <Edit size={16} className="mr-2" />
                  Edit Course
                </Link>
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <div className="bg-white/20 px-3 py-1 rounded">
                {course?.category}
              </div>
              <div className="bg-white/20 px-3 py-1 rounded">
                {course?.level}
              </div>
              <div className="bg-white/20 px-3 py-1 rounded">
                {course?.duration}
              </div>
              <div className="bg-white/20 px-3 py-1 rounded">
                {" ₹" + " " + course?.price}
              </div>
              <div
                className="bg-gray-200 px-4 py-1 text-gray-700 hover:bg-gray-300 hover:cursor-pointer rounded flex items-center cursor-pointer transition-all duration-200" // Added items-center for vertical alignment
                onClick={handleSyllabusDownload1}
              >
                <p className="flex items-center"> {/* Added flex and items-center here as well */}
                  Syllabus
                  <Download size={16} className="mr-2 ml-2" />
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-2">Course Description</h3>
              <p className="text-gray-600">{course?.description ?? course?.courseContent}</p>
            </div>
            <ModulesTab
              courseId={courseId || '1'}
              batches={batches ?? []}
              subjects={course?.subjects || []} // Pass the loaded course subjects  
              isReadonly={true}
              onSubjectsChange={(updatedSubjects) => setCourse({ ...course, subjects: updatedSubjects })} // Update state with new subjects  
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CourseDetails;  